import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";
import type { IPreset } from "@similarweb/ui-components";

import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";
import { isPaidExcludedFromPosition, isUnifiedPositionEnabled } from "app/components/campaigns/shared/utils";
import { translate } from "app/filters/i18nFilter";
import { fetchService } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import { getAPIPrefixForRT } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import {
    keywordsReportTableFiltersMap,
    type KeywordsReportTableFilter,
} from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/tablePresets/KeywordsReportTableFiltersConsts";
import { getApiParams } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportUtils";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery, useRTApiDuration } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { retryOnAbort } from "app/UtilitiesAndConstants/UtilityFunctions/query";

const getCitationPreset = (): IPreset<KeywordsReportTableFilter> => {
    if (isUnifiedPositionEnabled()) {
        return {
            get title() {
                return translate("rankTracker.keywords.presets.aioCitation");
            },
            get tooltipContent() {
                return translate("rankTracker.keywords.presets.aioCitation.tooltip");
            },
            id: keywordsReportTableFiltersMap.AIO_CITATION,
            data: undefined,
        };
    }

    return {
        get title() {
            return translate("rankTracker.keywords.presets.organicCannibalization");
        },
        get tooltipContent() {
            return translate("rankTracker.keywords.presets.organicCannibalization.tooltip");
        },
        id: keywordsReportTableFiltersMap.ORGANIC_CANNIBALIZATION,
        data: undefined,
    };
};

const getPaidCannibalizationPreset = (): IPreset<KeywordsReportTableFilter> => ({
    get title() {
        return translate("rankTracker.keywords.presets.paidCanibalization");
    },
    get tooltipContent() {
        return translate("rankTracker.keywords.presets.paidCanibalization.tooltip");
    },
    id: keywordsReportTableFiltersMap.PAID_CANNIBALIZATION,
    data: undefined,
});

export const shouldIncludePaidCannibalizationPreset = (
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
): boolean => {
    if (isUnifiedPositionEnabled()) {
        return isPaidExcludedFromPosition(positionExcludedSerpFeatures);
    }
    return true;
};

export const getPresetsBoxesConfig = (
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
): IPreset<KeywordsReportTableFilter>[] => {
    // Hide Paid Cannibalization when paid is included in position calculation:
    // otherwise this filter would likely match nearly all keywords with a paid
    // result in positions 1–3.
    const includePaidCannibalization = shouldIncludePaidCannibalizationPreset(positionExcludedSerpFeatures);

    return [
        {
            get title() {
                return translate("rankTracker.keywords.presets.trafficDrivers");
            },
            get tooltipContent() {
                return translate("rankTracker.keywords.presets.trafficDrivers.tooltip");
            },
            id: keywordsReportTableFiltersMap.TRAFFIC_DRIVERS,
            data: undefined,
        },
        {
            get title() {
                return translate("rankTracker.keywords.presets.opportunities");
            },
            get tooltipContent() {
                return translate("rankTracker.keywords.presets.opportunities.tooltip");
            },
            id: keywordsReportTableFiltersMap.OPPORTUNITIES,
            data: undefined,
        },
        getCitationPreset(),
        ...(includePaidCannibalization ? [getPaidCannibalizationPreset()] : []),
        {
            get title() {
                return translate("rankTracker.keywords.presets.targetUrlMissRank");
            },
            get tooltipContent() {
                return translate("rankTracker.keywords.presets.targetUrlMissRank.tooltip");
            },
            id: keywordsReportTableFiltersMap.TARGET_URL_MIS_RANK,
            data: undefined,
        },
        // in comment due we want have this filter in compare mode
        // {
        //     title: translate("rankTracker.keywords.presets.dropped"),
        //     tooltipContent: translate("rankTracker.keywords.presets.dropped.tooltip"),
        //     id: keywordsReportTableFiltersMap.DROPPED,
        //     data: undefined,
        // },
        // {
        //     title: translate("rankTracker.keywords.presets.winning"),
        //     tooltipContent: translate("rankTracker.keywords.presets.winning.tooltip"),
        //     id: keywordsReportTableFiltersMap.COMPETITOR_WINNING,
        //     data: undefined,
        // },
    ];
};

type KeywordsQuickFiltersData = {
    targetUrlMisRank: number;
    opportunities: number;
    dropped: number;
    aioCitation?: number;
    organicCannibalization?: number;
    paidCannibalization: number;
    competitorWinning: number;
    trafficDrivers: number;
};

type KeywordsQuickFiltersQueryParams = {
    // We pass it exclusivity (not via context) since this hook is called in Dashboards
    currentCampaignConfiguration: IRTCurrentCampaignData;
};

export const useKeywordsQuickFiltersQuery = ({ currentCampaignConfiguration }: KeywordsQuickFiltersQueryParams) => {
    const { params } = useCurrentStateAndParams<RTRawParams>();
    const { asc, sortedColumn, ...filterParams } = params;
    const { positionExcludedSerpFeatures } = currentCampaignConfiguration ?? {};

    const durationObject = useRTApiDuration(currentCampaignConfiguration);

    const { data, ...rest } = useRankTrackerQuery({
        queryKey: createCampaignQueryKey(currentCampaignConfiguration?.id, RankTrackerQueryType.KeywordsQuickFilters, [
            currentCampaignConfiguration?.keywordCount,
            filterParams,
        ]),
        enabled:
            !!currentCampaignConfiguration && currentCampaignConfiguration.id === params.campaignId && !!durationObject,
        queryFn: async ({ signal: cancellation }) => {
            if (!currentCampaignConfiguration || !durationObject) {
                return undefined;
            }
            const sitesParam = [
                currentCampaignConfiguration.site,
                ...currentCampaignConfiguration.competitors.map((competitor) => competitor.site),
            ]
                .filter((site) => site)
                .join(",");

            const apiParams = getApiParams(
                durationObject,
                currentCampaignConfiguration,
                params,
                params.graphGranularity,
                sitesParam,
            );

            return fetchService.get<KeywordsQuickFiltersData>(
                `${getAPIPrefixForRT(!!currentCampaignConfiguration.isDemo)}/reports/keywordsReport/UseCaseFilters`,
                apiParams,
                { cancellation },
            );
        },
        retry: retryOnAbort,
        throwOnError: (error) => {
            Sentry.error(`Error fetching table's filters for keywords report`, error);
            return false;
        },
    });

    const filtersData = useMemo(() => {
        const presetsBoxesConfig = getPresetsBoxesConfig(positionExcludedSerpFeatures);
        if (!data) {
            return presetsBoxesConfig;
        }
        return presetsBoxesConfig.map((item) => {
            return {
                ...item,
                data: data[item.id] ?? 0,
            };
        });
    }, [data, positionExcludedSerpFeatures]);

    return {
        data: filtersData,
        ...rest,
    };
};
